import { Component, type ReactNode } from "react";
import { logAppError } from "@/lib/error-logger";
import { reportLovableError } from "@/lib/lovable-error-reporting";

type Props = { children: ReactNode; label: string };
type State = { error: Error | null };

/**
 * Small per-section boundary — a failing section (wallet card, banner,
 * games list, live winners, etc.) shows a compact retry chip inside its
 * own slot instead of blanking the whole page. Tech details go to the
 * admin error log; the customer never sees a stack trace.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  componentDidCatch(error: Error, info: { componentStack?: string }) {
    try {
      reportLovableError(error, { boundary: `section:${this.props.label}` });
      logAppError({
        message: error?.message ?? String(error),
        stack: error?.stack ?? info?.componentStack ?? null,
        severity: "error",
        category: "frontend",
        meta: { boundary: `section:${this.props.label}` },
      });
    } catch { /* never crash the boundary */ }
  }
  reset = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-3 text-center text-xs">
        <p className="font-semibold text-destructive">Couldn't load this section.</p>
        <button
          onClick={this.reset}
          className="mt-2 rounded-full bg-primary px-3 py-1 text-[11px] font-bold text-primary-foreground"
        >
          Retry
        </button>
      </div>
    );
  }
}
